<template>
  <div class="oficina-main-screen">
    <!-- Sidebar -->
    <Sidebar
      :config="config"
      :current-step-index="currentStepIndex"
      :steps="steps"
      :collapsed="sidebarCollapsed"
      :mobile-open="mobileMenuOpen"
      :is-presentation-mode="isPresentationMode"
      @step-selected="handleStepSelected"
      @go-to-settings="$emit('go-to-settings')"
      @toggle="toggleSidebar"
      @close-mobile="closeMobileMenu"
    />

    <!-- Botão Flutuante para Recolher/Expandir Sidebar -->
    <button
      type="button"
      class="oficina-sidebar-toggle-floating"
      :class="{ 'collapsed': sidebarCollapsed }"
      @click="toggleSidebar"
      :title="sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'"
      aria-label="Recolher/Expandir menu"
    >
      <i :class="sidebarCollapsed ? 'icofont-arrow-right' : 'icofont-arrow-left'"></i>
    </button>

    <!-- Área Principal -->
    <div class="oficina-main-content">
      <!-- Botão Flutuante para Reabrir Menu -->
      <button
        type="button"
        class="oficina-sidebar-reopen-btn"
        id="btn-reopen-sidebar"
        aria-label="Mostrar menu"
        @click="toggleSidebar"
        v-if="sidebarCollapsed"
      >
        <span class="oficina-chevron oficina-chevron-right" aria-hidden="true"></span>
      </button>
      
      <!-- Overlay para fechar menu em mobile -->
      <div
        id="oficina-sidebar-overlay"
        class="oficina-sidebar-overlay"
        @click="closeMobileMenu"
        v-if="mobileMenuOpen"
      ></div>
      
      <!-- Botão Home Flutuante -->
      <div v-if="!isPresentationMode" class="oficina-home-btn">
        <button
          type="button"
          id="btn-home"
          title="Voltar ao Início / Configurações"
          @click="$emit('go-to-settings')"
        >
          <i class="icofont-home"></i>
        </button>
      </div>

      <!-- Barra de Progresso -->
      <div class="oficina-topbar">
        <div class="oficina-topbar-inner">
          <button
            type="button"
            id="btn-menu-mobile"
            class="oficina-menu-mobile-btn"
            aria-label="Abrir menu"
            @click="toggleMobileMenu"
          >
            <span class="oficina-hamburger" aria-hidden="true"></span>
          </button>

          <div class="oficina-progress-bar">
            <div
              class="oficina-progress-fill"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Conteúdo do Slide -->
      <div class="oficina-slide-container">
        <div class="oficina-slide-content">
          <component
            v-if="currentSlideComponent"
            :is="currentSlideComponent"
            :step="currentStep"
            :config="config"
            :active-tabs="activeTabs"
            :checklist="checklist"
            @update-tab="updateTab"
            @update-checklist="updateChecklist"
          />
          <div v-else>
            <p>Slide não implementado ainda.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from './Sidebar.vue'
import { getSteps } from '../steps'

export default {
  name: 'PresentationScreen',
  components: {
    Sidebar,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    currentStepIndex: {
      type: Number,
      default: 0,
    },
    isPresentationMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      activeTabs: {
        setup: 'visual',
        remote: 'video',
        remoteSetup: '',
        technicalSetup: 'whatsapp',
        esus: 'context',
        duvidas: 'access',
        eval: 'eval',
        sus: 'aps',
      },
      checklist: {},
    }
  },
  computed: {
    steps() {
      return getSteps(this.config)
    },
    currentStep() {
      return this.steps[this.currentStepIndex] || {}
    },
    progressPercentage() {
      return ((this.currentStepIndex + 1) / this.steps.length) * 100
    },
    currentSlideComponent() {
      const type = this.currentStep.type
      if (!type) return null
      
      // Mapear tipos para componentes
      const componentMap = {
        'script': () => import('./slides/ScriptSlide.vue'),
        'list': () => import('./slides/ListSlide.vue'),
        'tabbed-video': () => import('./slides/TabbedVideoSlide.vue'),
        'tabbed-interaction': () => import('./slides/TabbedInteractionSlide.vue'),
        'tabbed-content': () => import('./slides/TabbedContentSlide.vue'),
        'tabbed-evaluation': () => import('./slides/TabbedEvaluationSlide.vue'),
        'technical-setup': () => import('./slides/TechnicalSetupSlide.vue'),
        'remote-setup': () => import('./slides/RemoteSetupSlide.vue'),
        'facilitator-closing': () => import('./slides/FacilitatorClosingSlide.vue'),
      }
      
      return componentMap[type] || null
    },
  },
  mounted() {
    this.restoreSidebarState()
    // Garantir que a sidebar esteja no estado correto ao montar
    this.$nextTick(() => {
      const sidebar = document.querySelector('.oficina-sidebar')
      if (sidebar) {
        if (this.sidebarCollapsed) {
          sidebar.classList.add('hidden')
        } else {
          sidebar.classList.remove('hidden')
        }
      }
    })
  },
  beforeUnmount() {
    // Limpar event listeners
    const sidebar = document.querySelector('.oficina-sidebar')
    if (sidebar) {
      sidebar.removeEventListener('transitionend', this.handleSidebarTransitionEnd)
    }
  },
  methods: {
    handleStepSelected(index) {
      this.$emit('update-step', index)
      this.closeMobileMenu()
    },
    previousStep() {
      if (this.currentStepIndex > 0) {
        this.$emit('update-step', this.currentStepIndex - 1)
      }
    },
    nextStep() {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.$emit('update-step', this.currentStepIndex + 1)
      }
    },
    toggleSidebar() {
      const sidebar = document.querySelector('.oficina-sidebar')
      
      if (this.sidebarCollapsed) {
        // Se estava colapsada, expandir - mostrar imediatamente
        if (sidebar) {
          sidebar.classList.remove('hidden')
        }
        this.sidebarCollapsed = false
      } else {
        // Se estava expandida, recolher - ocultar após animação
        this.sidebarCollapsed = true
        if (sidebar) {
          // Aguardar fim da animação de opacity para ocultar completamente
          sidebar.addEventListener('transitionend', this.handleSidebarTransitionEnd, { once: true })
        }
      }
      
      this.saveSidebarState()
    },
    handleSidebarTransitionEnd(event) {
      // Ocultar quando a transição de opacity terminar (mais rápido que transform)
      if (event.propertyName === 'opacity') {
        const sidebar = event.target
        if (sidebar && sidebar.classList.contains('collapsed')) {
          sidebar.classList.add('hidden')
        }
      }
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    updateTab(group, tabId) {
      this.activeTabs = { ...this.activeTabs, [group]: tabId }
    },
    updateChecklist(key, value) {
      this.checklist = { ...this.checklist, [key]: value }
    },
    saveSidebarState() {
      try {
        localStorage.setItem('oficina-sidebar-collapsed', this.sidebarCollapsed ? 'true' : 'false')
      } catch (e) {
        console.warn('Não foi possível salvar o estado do sidebar:', e)
      }
    },
    restoreSidebarState() {
      if (window.innerWidth < 769) return
      
      try {
        const isCollapsed = localStorage.getItem('oficina-sidebar-collapsed') === 'true'
        this.sidebarCollapsed = isCollapsed
        
        // Aplicar classe hidden se estiver colapsada
        this.$nextTick(() => {
          const sidebar = document.querySelector('.oficina-sidebar')
          if (sidebar) {
            if (isCollapsed) {
              sidebar.classList.add('hidden')
            } else {
              sidebar.classList.remove('hidden')
            }
          }
        })
      } catch (e) {
        console.warn('Não foi possível restaurar o estado do sidebar:', e)
      }
    },
  },
}
</script>

<style scoped>
/* Estilos serão adicionados em arquivo CSS separado */
</style>
